<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-hdd-rack-fill" />
            </div>
            <div>
              <div class="assets-grid-title__text">
                {{ $t('aside.menu.pages.assetGroupsWithLocation') }}
              </div>
              <div class="text-muted small">
                {{ $t('components.assetGroupsWithLocation.subtitle') }}
              </div>
            </div>
          </div>

          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.refresh')"
              @click="fetchList"
            >
              <i class="bi bi-arrow-clockwise" />
              <span>{{ $t('components.assets.dataGrids.refresh') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <div
            v-if="listError"
            class="alert alert-danger d-flex align-items-center gap-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            <span>{{ listError }}</span>
          </div>
          <div
            v-if="listLoading"
            class="d-flex align-items-center gap-3 py-3"
          >
            <span
              class="spinner-border text-primary"
              role="status"
            />
            <span>{{ $t('components.assetGroupsWithLocation.loading') }}</span>
          </div>
          <div v-else>
            <DxDataGrid
              class="agwl-grid"
              :data-source="listData"
              :hover-state-enabled="true"
              :column-auto-width="false"
              :show-borders="false"
              :show-column-lines="false"
              :show-row-lines="false"
              :row-alternation-enabled="true"
              :row-rendering-mode="'standard'"
              :column-hiding-enabled="true"
              :word-wrap-enabled="true"
            >
              <DxHeaderFilter :visible="true" />
              <DxFilterRow :visible="true" />
              <DxPaging :page-size="20" />
              <DxPager
                :allowed-page-sizes="[10, 20, 50]"
                :show-info="true"
                :show-page-size-selector="true"
                :show-navigation-buttons="true"
              />

              <DxColumn
                data-field="name"
                :caption="$t('components.assetGroupsWithLocation.columns.name')"
                :min-width="180"
              />
              <DxColumn
                data-field="locationImages"
                :caption="$t('components.assetGroupsWithLocation.columns.locationImage')"
                width="160"
                :allow-filtering="false"
                :allow-sorting="false"
                cell-template="location-image"
                alignment="center"
                :hiding-priority="2"
              />
              <DxColumn
                data-field="schemesCount"
                :caption="$t('components.assetGroupsWithLocation.columns.schemesCount')"
                alignment="center"
                width="140"
                :allow-filtering="false"
                :hiding-priority="3"
              />
              <DxColumn
                data-field="locationText"
                :caption="$t('components.assetGroupsWithLocation.columns.location')"
                :min-width="160"
                alignment="center"
                :allow-filtering="false"
                :hiding-priority="1"
              />
              <DxColumn
                :caption="$t('components.assetGroupsWithLocation.columns.actions')"
                :width="150"
                :allow-filtering="false"
                :allow-sorting="false"
                cell-template="actions-template"
                :hiding-priority="4"
              />

              <template #location-image="{ data }">
                <div class="location-thumb">
                  <img
                    v-for="(img, idx) in data.data.locationImages"
                    :key="idx"
                    :src="img"
                    alt="Location thumbnail"
                    class="location-thumb__img"
                  >
                  <span
                    v-if="!data.data.locationImages?.length"
                    class="text-muted"
                  >-</span>
                </div>
              </template>

              <template #actions-template="{ data }">
                <div class="action-buttons">
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :title="$t('components.assetGroupsWithLocation.actions.edit')"
                    @click="openEditModal(data.data)"
                  >
                    <i class="bi bi-pencil" />
                  </button>
                  <button
                    v-if="canEdit"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :title="$t('components.assetGroupsWithLocation.actions.delete')"
                    @click="confirmDelete(data.data)"
                  >
                    <i class="bi bi-trash" />
                  </button>
                  <button
                    v-if="isReadOnly"
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :title="$t('components.assetGroupsWithLocation.actions.view')"
                    @click="openViewModal(data.data)"
                  >
                    <i class="bi bi-eye" />
                  </button>
                </div>
              </template>
            </DxDataGrid>
            <div
              v-if="!listData.length"
              class="text-center text-muted py-3"
            >
              {{ $t('components.assetGroupsWithLocation.empty') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Asset Group With Location Form Modal -->
    <AssetGroupWithLocationFormModal
      ref="formModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useQuasar } from 'quasar'
import AssetGroupWithLocationFormModal from '@/components/content/_modals/AssetGroupWithLocationFormModal.vue'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager
} from 'devextreme-vue/data-grid'
import { getAssetGroupsWithLocation, getAssetGroupLocation, deleteAssetGroup } from '@/crud/asset-group'
import { canEditAssetGroups, isRestrictedAssetUser } from '@/utils/tools'

type FlatRow = {
  id: number
  name: string
  locationImages: string[]
  schemesCount: number
  street?: string | null
  city?: string | null
  postalCode?: string | null
  country?: string | null
  locationText: string
}

export default defineComponent({
  name: 'AssetGroupsWithLocationView',
  components: {
    AssetGroupWithLocationFormModal,
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager
  },
  emits: ['pageName'],
  data() {
    return {
      listData: [] as FlatRow[],
      listLoading: false,
      listError: null as string | null,
      quasar: useQuasar()
    }
  },
  computed: {
    canEdit(): boolean {
      return canEditAssetGroups()
    },
    isReadOnly(): boolean {
      return isRestrictedAssetUser()
    }
  },
  watch: {
    '$i18n.locale'() {
      this.setTitle()
    }
  },
  mounted() {
    this.setTitle()
    this.fetchList()
  },
  methods: {
    setTitle() {
      this.$emit('pageName', this.$t('aside.menu.pages.assetGroupsWithLocation'))
    },

    convertPathToUrl(filePath: string): string {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    },

    async fetchList() {
      this.listLoading = true
      this.listError = null
      try {
        const response = await getAssetGroupsWithLocation()
        const groups = response.data.data?.result || []

        this.listData = groups.map((group: any) => {
          const location = group.location || {}
          const images = Array.isArray(location.images) ? location.images : []
          const imagePath = location.imagePath || location.image_path
          const imagesFallback = imagePath ? [imagePath] : []
          const resolvedImages = (images.length ? images : imagesFallback)
            .map((path: string) => this.convertPathToUrl(path))
            .slice(0, 2)
          const locationParts = [
            location.street,
            location.postalCode ? `${location.postalCode}` : null,
            location.city,
            location.country
          ].filter(Boolean)

          return {
            id: group.id,
            name: group.name,
            schemesCount: group.schemesCount ?? 0,
            locationImages: resolvedImages,
            street: location.street || null,
            city: location.city || null,
            postalCode: location.postalCode || null,
            country: location.country || null,
            locationText: locationParts.join(', ') || '-'
          }
        })
      } catch (e: any) {
        this.listError = e?.response?.data?.error || e?.message || this.$t('components.assetGroupsWithLocation.error')
      } finally {
        this.listLoading = false
      }
    },

    async openEditModal(row: FlatRow) {
      try {
        const response = await getAssetGroupLocation(row.id)
        const groupData = response.data.group
        if (!groupData) {
          throw new Error(this.$t('components.assetGroupsWithLocation.error') as string)
        }

        (this.$refs.formModal as any)?.openModal(groupData)
      } catch (e: any) {
        this.listError = e?.response?.data?.error || e?.message || this.$t('components.assetGroupsWithLocation.error')
      }
    },

    async openViewModal(row: FlatRow) {
      try {
        const response = await getAssetGroupLocation(row.id)
        const groupData = response.data.group
        if (!groupData) {
          throw new Error(this.$t('components.assetGroupsWithLocation.error') as string)
        }

        (this.$refs.formModal as any)?.openModal(groupData, true)
      } catch (e: any) {
        this.listError = e?.response?.data?.error || e?.message || this.$t('components.assetGroupsWithLocation.error')
      }
    },

    confirmDelete(row: FlatRow) {
      this.quasar.dialog({
        title: this.$t('components.assetGroupsWithLocation.actions.deleteTitle'),
        message: this.$t('components.assetGroupsWithLocation.actions.confirmDelete'),
        cancel: true
      }).onOk(async () => {
        try {
          await deleteAssetGroup(row.id)

          this.$toast?.success(this.$t('components.assetGroupsWithLocation.actions.deleteSuccess'))

          await this.fetchList()
        } catch (e: any) {
          const errorMsg = e?.response?.data?.error || e?.message || this.$t('components.assetGroupsWithLocation.actions.deleteError')
          this.listError = errorMsg
          this.$toast?.error(errorMsg)
        }
      })
    },

    async handleSaved() {
      await this.fetchList()
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-groups-with-location-view.scss';
</style>
